import { z } from "zod";
import { MEASUREMENT_BOUNDS } from "./health";

// TES-1649 (Ö-1014, Ö-1018) — the waist measurement on the written track, as
// its own task after the health declaration.
//
// F02, "KANONISKT BESLUT 2026-10-28 — MIDJEMÅTT VID BMI 25,0–26,9 UTAN
// VÅRDMÖTE":
//   * "För nybehandlingspatienter med beräknat BMI 25,0–26,9 som går vidare
//     utan vårdmöte är midjemått obligatoriskt före läkarens slutliga
//     skriftliga bedömning." BMI 27+, a meeting and a transfer: optional,
//     unless the doctor asks for it;
//   * "Ett befintligt giltigt WAIST_CM-värde som är högst sju dagar gammalt
//     kan återanvändas efter aktiv bekräftelse." "Saknas ett färskt värde
//     måste ett nytt värde registreras";
//   * "Måttet sparas i den gemensamma WAIST_CM-serien och får inte
//     dupliceras": a new value is a MeasurementSession in the series; a
//     confirmed one is a pointer to the session she confirmed;
//   * "Status skiljer på required_pending, submitted, approved,
//     retake_required och meeting_selected." The doctor's review of the
//     measurement (approved, retake_required) is not built: the doctor reads
//     it in the low-BMI panel as decision support, "inte som automatiskt
//     utfall".

/** The newest a reading may be offered from, and a new one dated, in days. */
export const ASSESSMENT_WAIST_FRESH_DAYS = 7;

// not_required     — the task does not apply to her (not BMI 25,0–26,9, not
//                    on the written track, or not a new treatment);
// required_pending — it applies and nothing is sent;
// submitted        — she sent a measurement (new or confirmed);
// meeting_selected — BMI 25,0–26,9 and she chose, or was given, the meeting
//                    track instead: the waist is optional there.
export const ASSESSMENT_WAIST_STATUSES = [
  "not_required",
  "required_pending",
  "submitted",
  "meeting_selected",
] as const;
export const assessmentWaistStatusSchema = z.enum(ASSESSMENT_WAIST_STATUSES);
export type AssessmentWaistStatus = z.infer<typeof assessmentWaistStatusSchema>;

export const assessmentWaistReadingSchema = z.object({
  sessionId: z.string(),
  waistCm: z.number(),
  /** The day it was measured, YYYY-MM-DD in the tenant's zone. */
  measuredOn: z.string(),
});
export type AssessmentWaistReading = z.infer<
  typeof assessmentWaistReadingSchema
>;

/** GET patient-api/v1/me/assessment-waist */
export const patientAssessmentWaistViewSchema = z.object({
  required: z.boolean(),
  status: assessmentWaistStatusSchema,
  /** Her newest waist from the last seven days, offered for confirmation. */
  suggestion: assessmentWaistReadingSchema.nullable(),
  submitted: assessmentWaistReadingSchema
    .extend({ submittedAt: z.string() })
    .nullable(),
});
export type PatientAssessmentWaistView = z.infer<
  typeof patientAssessmentWaistViewSchema
>;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * POST patient-api/v1/me/assessment-waist: either the fresh reading she
 * confirmed ("Godkänn"), or a new one in centimetres with the day she
 * measured it.
 */
export const submitAssessmentWaistSchema = z.union([
  z.object({ confirmSessionId: z.string().uuid() }).strict(),
  z
    .object({
      waistCm: z
        .number()
        .min(MEASUREMENT_BOUNDS.waist.min)
        .max(MEASUREMENT_BOUNDS.waist.max),
      measuredOn: z.string().regex(ISO_DAY),
    })
    .strict(),
]);
export type SubmitAssessmentWaist = z.infer<typeof submitAssessmentWaistSchema>;
